import React, {
  CSSProperties,
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import { getPreviewEntityUniqueElementId, stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { FileIcon, LoadingSpinner } from "@mapmycustomers/ui";

import ActivityTypeChip from "@app/component/activity/ActivityTypeChip";
import Chip from "@app/component/Chip";
import EntityChip from "@app/component/EntityChip";
import isMediaFile from "@app/component/FilePreview/isMediaFile";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { downloadFile, fetchFile } from "@app/store/files/actions";
import {
  TimelineEventFileAddedInActivity,
  TimelineEventFileAddedInRecord,
} from "@app/types/events/TimelineEvent";
import useAnalytics from "@app/util/contexts/useAnalytics";
import hasMimeType, { IMAGE_ANY } from "@app/util/mimeType";

import BaseEvent from "./BaseEvent";
import FilePreview from "./components/FilePreview";
import styles from "./RecordEvent.module.scss";

export interface RecordEventProps {
  event: TimelineEventFileAddedInActivity | TimelineEventFileAddedInRecord;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  onDownloadFile: typeof downloadFile;
  onFetchFile: typeof fetchFile;
  onShowEntityView: (payload: EntityViewPayload) => void;
}

const FileAddedEvent = forwardRef<HTMLDivElement, Props>(
  (
    { event, expanded, onDownloadFile, onFetchFile, onShowEntityView, onSizeChange, style },
    ref
  ) => {
    const analytics = useAnalytics();
    const intl = useIntl();

    let record: Pick<Company | Deal | Person, "entity" | "id" | "name"> | undefined = undefined;
    if (event.entity.entity === EntityType.ACTIVITY) {
      const association = (event as TimelineEventFileAddedInActivity).associatedEntity?.[0];
      if (association) {
        record = { id: association.id, entity: association.entity, name: association.name };
      }
    } else {
      record = event.entity;
    }

    const isImageFile = hasMimeType(event.file, [...IMAGE_ANY]);

    const [loading, setLoading] = useState<boolean>(false);
    const [file, setFile] = useState<RawFile | undefined>();
    const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>();
    const [previewVisible, showPreview, hidePreview] = useBoolean();

    useEffect(() => {
      setLoading(true);
      onFetchFile({
        entityId: event.entity.id,
        entityType: event.entity.entity,
        fileId: event.file.id,
        onFailure: () => {
          setLoading(false);
          // also, "not found" notification is prevented when onFailure callback is present
        },
        onSuccess: (file: RawFile, blob: Blob) => {
          setFile(file);
          setThumbnailUrl((previousUrl) => {
            // shouldn't really happen, but let's take care of this anyway
            if (previousUrl) {
              window.URL.revokeObjectURL(previousUrl);
            }
            return window.URL.createObjectURL(blob);
          });
          setLoading(false);
        },
      });
    }, [event, onFetchFile]);

    // don't forget to revoke the URL when component is unmounted
    useEffect(
      () => () => {
        if (thumbnailUrl) {
          window.URL.revokeObjectURL(thumbnailUrl);
        }
      },
      [thumbnailUrl]
    );

    const handleFileClick = useCallback(() => {
      if (file && isMediaFile(file)) {
        analytics.clicked(["File"]);
        showPreview();
      } else {
        analytics.clicked(["File", "download"]);
        onDownloadFile({
          entityId: event.entity.id,
          entityType: event.entity.entity,
          fileId: event.file.id,
        });
      }
    }, [event, file, onDownloadFile, showPreview, analytics]);

    const activityTypeChipRef = useRef<HTMLDivElement>(null);
    const handleActivityClick = useCallback(
      (_, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        const elementId = getPreviewEntityUniqueElementId(activityTypeChipRef.current || undefined);
        onShowEntityView({ elementId, entityId: event.entity.id, entityType: event.entity.entity });
      },
      [event, onShowEntityView]
    );

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    useLayoutEffect(() => {
      if ((isImageFile && thumbnailUrl) || loading) {
        setTimeout(() => onSizeChange?.(), 0);
      }
    }, [isImageFile, loading, onSizeChange, thumbnailUrl]);

    return (
      <BaseEvent
        className={isImageFile ? styles.thumbnailContainer : undefined}
        date={event.updatedAt}
        expanded={expanded}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.fileAdded",
            defaultMessage:
              "uploaded {isImageFile, select, true {an image} other {a file}} to {isActivity, select, true {{activityType}{record, select, false {} other { with {record}}}} other {{record}}}",
            description: "Title of the File Added event item",
          },
          {
            activityType:
              event.entity.entity === EntityType.ACTIVITY ? (
                <ActivityTypeChip
                  activityType={(event as TimelineEventFileAddedInActivity).entity.crmActivityType}
                  isAutoEmail={!!(event as TimelineEventFileAddedInActivity).entity.emailLog}
                  onClick={handleActivityClick}
                  ref={activityTypeChipRef}
                />
              ) : (
                ""
              ),
            isActivity: event.entity.entity === EntityType.ACTIVITY,
            isImageFile,
            record: record ? (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ) : (
              false
            ),
          }
        )}
        user={event.updatedBy}
      >
        <div className={styles.image}>
          {file ? (
            <Chip
              icon={<FileIcon className={styles.fileIcon} file={file} />}
              inline
              onClick={handleFileClick}
            >
              {file.name}
            </Chip>
          ) : undefined}
          {isImageFile && thumbnailUrl ? (
            <img
              alt={file?.name}
              className={styles.thumbnail}
              onClick={handleFileClick}
              src={thumbnailUrl}
            />
          ) : loading ? (
            <LoadingSpinner mini />
          ) : undefined}
        </div>
        {previewVisible && file && thumbnailUrl ? (
          <FilePreview contentUrl={thumbnailUrl} file={file} onHide={hidePreview} />
        ) : null}
      </BaseEvent>
    );
  }
);

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  onDownloadFile: downloadFile,
  onFetchFile: fetchFile,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  FileAddedEvent
);
